import { useState, useCallback } from "react";
import { Col, Form, Row, Select } from "antd";
import { debounce } from "lodash";

export interface AddressSearchResult {
  place_id: string;
  display_name: string;
  lon: number;
  lat: number;
  geometry: Array<{
    attributes: {
      ADRESSE: string;
      NO_POSTAL: string;
      NOM_NPA: string;
    };
  }>;
}

export interface ParcelleData {
  NoCommune: string;
  NoParcelle: string;
  CommuneCadastre: string;
  m2_terrain?: number;
}

export interface AddressFormValues {
  Adresse: string;
  NPA: string;
  CommuneAdresse: string;
  NoCommune: string;
  NoParcelle: string;
  CommuneCadastre: string;
  Position_sur_map: {
    type: "Point";
    coordinates: [number, number];
  };
  m2_terrain?: number;
  Quartier?: string;
  SectVille?: string;
  Rive?: string;
}

export interface UseAddressManagerProps {
  onAddressSelected?: (values: Partial<AddressFormValues>) => void;
}

export const useAddressManager = ({ onAddressSelected }: UseAddressManagerProps = {}) => {
  const [addressSearchValue, setAddressSearchValue] = useState("");
  const [addressSearchResults, setAddressSearchResults] = useState<AddressSearchResult[]>([]);
  const [loadingAddress, setLoadingAddress] = useState(false);

  // Address search functionality
  const searchAddress = useCallback(async (address: string) => {
    if (!address.trim()) {
      setAddressSearchResults([]);
      return;
    }

    setLoadingAddress(true);
    try {
      const response = await fetch(
        `https://melcarnebackend.digipulse.ch/geocoder?s=${encodeURIComponent(address)}`,
        { cache: 'no-store' }
      );
      const data = await response.json();
      setAddressSearchResults(data || []);
    } catch (error) {
      console.error('Error searching address:', error);
      setAddressSearchResults([]);
    } finally {
      setLoadingAddress(false);
    }
  }, []);

  const debouncedSearchAddress = useCallback(
    debounce((address: string) => {
      console.log('Debounced search called for:', address);
      searchAddress(address);
    }, 500),
    [searchAddress]
  );

  // Get parcelle data from coordinates using SITG identify
  const getParcelleData = async (lon: number, lat: number): Promise<ParcelleData | null> => {
    try {
      const url = `https://melcarnebackend.digipulse.ch/generate_sitg_identify_link?lon=${lon}&lat=${lat}&filter_by_point=true`;
      console.log('getParcelleData URL:', url);
      const response = await fetch(url, { cache: 'no-store' });
      const json = await response.json();
      console.log('getParcelleData raw response:', json);

      const results = json?.data?.results;
      if (!results || results.length === 0) {
        console.warn('getParcelleData: No results in SITG response');
        return null;
      }

      const data = results[0].attributes;
      console.log('getParcelleData attributes:', data);

      if (data) {
        return {
          NoCommune: data["NO COMMUNE"],
          NoParcelle: data["NO PARCELLE"],
          CommuneCadastre: data.COMMUNE,
          m2_terrain: data.SURFACE
        };
      }
    } catch (error) {
      console.error('Error fetching parcelle data:', error);
    }
    return null;
  };

  // Get Quartier, SectVille, and Rive data from coordinates
  const getQuartierSectVilleRiveData = async (lon: number, lat: number, parcelleData: ParcelleData): Promise<{Quartier?: string, SectVille?: string, Rive?: string}> => {
    try {
      const response = await fetch(
        `https://melcarnebackend.digipulse.ch/get_parcelle_address?commune_cadastre=${encodeURIComponent(parcelleData.CommuneCadastre)}&no_commune=${encodeURIComponent(parcelleData.NoCommune)}&no_parcelle=${encodeURIComponent(parcelleData.NoParcelle)}`,
        { cache: 'no-store' }
      );
      const data = await response.json();

      return {
        Quartier: data.Quartier || undefined,
        SectVille: data.SectVille || undefined,
        Rive: data.Rive || undefined
      };
    } catch (error) {
      console.error('Error fetching Quartier, SectVille, and Rive data:', error);
      return {};
    }
  };

  // Handle address selection
  const handleAddressSelection = async (placeId: string) => {
    const selectedPlace = addressSearchResults.find(r => r.place_id === placeId);
    if (!selectedPlace) return;

    setAddressSearchValue(selectedPlace.display_name);
    setAddressSearchResults([]);

    console.log('Selected place:', JSON.stringify(selectedPlace, null, 2));

    const lon = parseFloat(String(selectedPlace.lon));
    const lat = parseFloat(String(selectedPlace.lat));

    const geoAttrs = selectedPlace.geometry?.[0]?.attributes;

    const updatedValues: Partial<AddressFormValues> = {
      Position_sur_map: {
        type: "Point",
        coordinates: [lon, lat]
      }
    };

    if (geoAttrs) {
      updatedValues.Adresse = geoAttrs.ADRESSE;
      updatedValues.NPA = geoAttrs.NO_POSTAL;
      updatedValues.CommuneAdresse = geoAttrs.NOM_NPA;
    } else {
      // Fallback: extract from display_name (e.g. "Route de Malagnou 53 - 1208 Genève [...]")
      console.warn('geometry attributes missing, falling back to display_name');
      const match = selectedPlace.display_name?.match(/^(.+?)\s*-\s*(\d+)\s+(.+?)\s*\[/);
      if (match) {
        updatedValues.Adresse = match[1].trim();
        updatedValues.NPA = match[2];
        updatedValues.CommuneAdresse = match[3].trim();
      }
    }

    // Try to fetch parcelle data
    const parcelleData = await getParcelleData(lon, lat);
    console.log('Parcelle data:', parcelleData);

    if (parcelleData) {
      updatedValues.NoCommune = parcelleData.NoCommune;
      updatedValues.NoParcelle = parcelleData.NoParcelle;
      updatedValues.CommuneCadastre = parcelleData.CommuneCadastre;

      if (parcelleData.m2_terrain) {
        updatedValues.m2_terrain = parcelleData.m2_terrain;
      }

      // Fetch Quartier, SectVille, and Rive data
      const quartierSectVilleRiveData = await getQuartierSectVilleRiveData(lon, lat, parcelleData);
      if (quartierSectVilleRiveData.Quartier) {
        updatedValues.Quartier = quartierSectVilleRiveData.Quartier;
      }
      if (quartierSectVilleRiveData.SectVille) {
        updatedValues.SectVille = quartierSectVilleRiveData.SectVille;
      }
      if (quartierSectVilleRiveData.Rive) {
        updatedValues.Rive = quartierSectVilleRiveData.Rive;
      }
    } else {
      console.warn('Parcelle data unavailable — only basic address fields will be set');
    }

    console.log('Final updatedValues:', updatedValues);

    if (onAddressSelected) {
      onAddressSelected(updatedValues);
    }

    return updatedValues;
  };

  const clearAddressSearch = () => {
    setAddressSearchValue("");
    setAddressSearchResults([]);
  };

  return {
    addressSearchValue,
    setAddressSearchValue,
    addressSearchResults,
    loadingAddress,
    searchAddress,
    debouncedSearchAddress,
    handleAddressSelection,
    clearAddressSearch,
    getParcelleData
  };
};

export interface AddressSearchFieldProps {
  span?: number;
  onAddressSelected?: (values: Partial<AddressFormValues>) => void;
}

export const AddressSearchField: React.FC<AddressSearchFieldProps> = ({ 
  span = 24, 
  onAddressSelected 
}) => {
  const {
    addressSearchValue,
    setAddressSearchValue,
    addressSearchResults,
    loadingAddress,
    debouncedSearchAddress,
    handleAddressSelection,
    clearAddressSearch
  } = useAddressManager({ onAddressSelected });

  return (
    <Row gutter={[16, 16]}>
      <Col span={span}>
        <Form.Item label="Rechercher une adresse">
          <Select
            showSearch
            style={{ width: "100%" }}
            placeholder="Entrer une adresse"
            defaultActiveFirstOption={false}
            suffixIcon={null}
            filterOption={false}
            onSearch={(value) => {
              setAddressSearchValue(value);
              if (value.trim()) {
                debouncedSearchAddress(value);
              } else {
                clearAddressSearch();
              }
            }}
            onChange={handleAddressSelection}
            notFoundContent={loadingAddress ? "Recherche en cours..." : "Aucun résultat"}
            options={addressSearchResults.map((result: AddressSearchResult) => ({
              label: result.display_name,
              value: result.place_id,
            }))}
            allowClear
            value={addressSearchValue || undefined}
            onClear={clearAddressSearch}
          />
        </Form.Item>
      </Col>
    </Row>
  );
};
